import React, { useState } from 'react';
import { Modal, Button, Alert, Card, Row, Col, Form, Badge, ListGroup } from 'react-bootstrap';
import { AlertTriangle, Trash2, Store, TrendingDown, CreditCard, Calendar, Package, RotateCcw, Activity, Receipt } from 'lucide-react';

/**
 * ✅ MISE À JOUR : Modal de confirmation pour la suppression complète (ventes + stocks + mouvements)
 */
const DeleteSalesConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  boutique, 
  confirmationData, 
  loading = false 
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState(1); // 1: Avertissement, 2: Confirmation finale

  const requiredConfirmText = `SUPPRIMER TOUTES LES DONNEES DE ${boutique?.name?.toUpperCase() || ''}`;
  const isConfirmTextValid = confirmText === requiredConfirmText;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleNextStep = () => {
    if (step === 1) {
      setStep(2);
    }
  };

  const handleConfirm = () => {
    if (isConfirmTextValid) {
      onConfirm();
      setConfirmText('');
      setStep(1);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setStep(1);
    onClose();
  };

  return (
    <Modal 
      show={isOpen} 
      onHide={handleClose} 
      size="lg" 
      backdrop="static"
      keyboard={false}
      centered
    >
      {/* Header avec alerte critique */}
      <Modal.Header className="bg-danger text-white border-0" closeButton>
        <Modal.Title className="d-flex align-items-center w-100">
          <AlertTriangle size={28} className="text-warning me-3" />
          <div className="flex-grow-1">
            <div className="fs-5 mb-1">
              ⚠️ SUPPRESSION COMPLÈTE - ACTION CRITIQUE
            </div>
            <small className="opacity-75">
              ✅ NOUVEAU : Suppression des ventes + remise à zéro des stocks + suppression des mouvements
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {step === 1 && (
          <>
            {/* Étape 1: Avertissement et statistiques */}
            
            {/* Alerte principale */}
            <Alert variant="danger" className="d-flex align-items-start">
              <AlertTriangle size={20} className="me-2 mt-1 flex-shrink-0" />
              <div>
                <h6 className="alert-heading mb-2">
                  ✅ NOUVEAU : Vous allez TOUT supprimer pour la boutique :
                </h6>
                <div className="d-flex align-items-center">
                  <Store size={18} className="me-2" />
                  <strong className="fs-5">{boutique?.name}</strong>
                </div>
                <ul className="mt-2 mb-0 small">
                  <li>🗑️ <strong>TOUTES les ventes</strong> seront supprimées définitivement</li>
                  <li>🔄 <strong>TOUS les stocks</strong> seront remis à zéro</li>
                  <li>📊 <strong>TOUS les mouvements de stock</strong> seront supprimés</li>
                  <li>💰 <strong>L'historique des ventes</strong> sera perdu</li>
                </ul>
              </div>
            </Alert>

            {/* Statistiques à supprimer */}
            {confirmationData && (
              <>
                <h6 className="mb-3">📊 Données qui seront supprimées :</h6>
                
                <Row className="mb-4">
                  {/* Ventes */}
                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-danger">
                      <Card.Body className="text-center">
                        <Trash2 size={24} className="text-danger mb-2" />
                        <h5 className="text-danger mb-1">
                          {confirmationData.totalSalesCount?.toLocaleString('fr-FR')}
                        </h5>
                        <small className="text-muted">ventes à supprimer</small>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-danger">
                      <Card.Body className="text-center">
                        <TrendingDown size={24} className="text-danger mb-2" />
                        <h5 className="text-danger mb-1">
                          {formatCurrency(confirmationData.totalRevenue)}
                        </h5>
                        <small className="text-muted">chiffre d'affaires perdu</small>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* ✅ NOUVEAU : Stocks */}
                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-warning">
                      <Card.Body className="text-center">
                        <Package size={24} className="text-warning mb-2" />
                        <h5 className="text-warning mb-1">
                          {confirmationData.totalProducts?.toLocaleString('fr-FR')}
                        </h5>
                        <small className="text-muted">produits remis à stock zéro</small>
                        <div className="mt-1">
                          <Badge bg="secondary" className="small">
                            {confirmationData.totalStockQuantity?.toLocaleString('fr-FR')} unités actuelles
                          </Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-warning">
                      <Card.Body className="text-center">
                        <RotateCcw size={24} className="text-warning mb-2" />
                        <h5 className="text-warning mb-1">
                          {formatCurrency(confirmationData.totalStockValue)}
                        </h5>
                        <small className="text-muted">valeur stock perdue</small>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* ✅ NOUVEAU : Mouvements */}
                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-info">
                      <Card.Body className="text-center">
                        <Activity size={24} className="text-info mb-2" />
                        <h5 className="text-info mb-1">
                          {confirmationData.totalMovements?.toLocaleString('fr-FR')}
                        </h5>
                        <small className="text-muted">mouvements de stock supprimés</small>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-warning">
                      <Card.Body className="text-center">
                        <CreditCard size={24} className="text-warning mb-2" />
                        <h5 className="text-warning mb-1">
                          {confirmationData.creditSalesCount?.toLocaleString('fr-FR')}
                        </h5>
                        <small className="text-muted">ventes à crédit perdues</small>
                        <div className="mt-1">
                          <Badge bg="warning" className="small">
                            {formatCurrency(confirmationData.totalCreditAmount)}
                          </Badge>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* ✅ NOUVEAU : Nombre de paiements */}
{confirmationData?.totalPaymentsCount > 0 && (
  <Col md={6} className="mb-3">
    <Card className="h-100 border-info">
      <Card.Body className="text-center">
        <Receipt size={24} className="text-info mb-2" />
        <h5 className="text-info mb-1">
          {confirmationData.totalPaymentsCount}
        </h5>
        <small className="text-muted">
          paiement{confirmationData.totalPaymentsCount > 1 ? 's' : ''} enregistré{confirmationData.totalPaymentsCount > 1 ? 's' : ''}
        </small>
        <div className="mt-2">
          <small className="text-muted d-block">
            (Historique des encaissements)
          </small>
        </div>
      </Card.Body>
    </Card>
  </Col>
)}

                {/* Informations temporelles */}
                {(confirmationData.oldestSaleDate || confirmationData.newestSaleDate) && (
                  <Alert variant="info" className="mb-3">
                    <Calendar size={16} className="me-2" />
                    <strong>Période des ventes :</strong> du {formatDate(confirmationData.oldestSaleDate)} au {formatDate(confirmationData.newestSaleDate)}
                  </Alert>
                )}

                {/* Ventes récentes */}
                {confirmationData.recentSales && confirmationData.recentSales.length > 0 && (
                  <div className="mb-3">
                    <h6>🕐 Dernières ventes (qui seront supprimées) :</h6>
                    <ListGroup variant="flush" className="border rounded">
                      {confirmationData.recentSales.slice(0, 3).map((sale, index) => (
                        <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center py-2">
                          <div>
                            <strong>#{sale.saleNumber}</strong>
                            <br />
                            <small className="text-muted">{sale.customerName}</small>
                          </div>
                          <div className="text-end">
                            <div>{formatCurrency(sale.total)}</div>
                            <small className="text-muted">{formatDate(sale.createdAt)}</small>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </>
            )}

            {/* Conséquences détaillées */}
            <Alert variant="warning">
              <h6>⚠️ Conséquences IRRÉVERSIBLES :</h6>
              <ul className="mb-0 small">
                <li>📊 Perte complète de l'historique des ventes</li>
                <li>💰 Perte des données de chiffre d'affaires</li>
                <li>📦 Remise à zéro de tous les stocks produits</li>
                <li>🔄 Suppression de l'historique des mouvements de stock</li>
                <li>💳 Perte des informations sur les crédits clients</li>
                <li>📈 Impossible de restaurer les statistiques</li>
              </ul>
            </Alert>
            <Alert variant="danger" className="mb-4">
  <h6>⚠️ Cette action supprimera DÉFINITIVEMENT :</h6>
  <ul className="mb-0">
    <li><strong>{confirmationData?.totalSalesCount || 0}</strong> ventes</li>
    
    {/* ✅ NOUVEAU : Ligne pour les paiements */}
    {confirmationData?.totalPaymentsCount > 0 && (
      <li><strong>{confirmationData.totalPaymentsCount}</strong> paiements enregistrés (historique complet des encaissements)</li>
    )}
    
    <li><strong>{confirmationData?.totalMovements || 0}</strong> mouvements de stock</li>
    <li>Remise à <strong>zéro</strong> du stock de <strong>{confirmationData?.totalProducts || 0}</strong> produits</li>
    <li>Réinitialisation des totaux d'achats des clients</li>
    <li><strong>Toutes les données associées</strong></li>
  </ul>
  <div className="mt-3 p-2 bg-white rounded">
    <strong className="text-danger">⚠️ ATTENTION :</strong> Les paiements de crédit, les paiements mixtes et tout l'historique d'encaissement seront <strong>définitivement perdus</strong>.
  </div>
</Alert>

          </>
        )}

        {step === 2 && (
          <>
            {/* Étape 2: Confirmation finale */}
            <Alert variant="danger" className="text-center">
              <AlertTriangle size={32} className="mb-2" />
              <h5>CONFIRMATION FINALE REQUISE</h5>
              <p className="mb-0">Cette action va DÉFINITIVEMENT supprimer toutes les données de vente et remettre tous les stocks à zéro</p>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                ✅ NOUVEAU : Pour confirmer la suppression complète, tapez exactement :
              </Form.Label>
              <code className="d-block p-2 bg-light border rounded mb-2 small">
                {requiredConfirmText}
              </code>
              <Form.Control
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Tapez le texte ci-dessus..."
                disabled={loading}
                className={isConfirmTextValid ? 'border-success' : 'border-danger'}
              />
              {confirmText && !isConfirmTextValid && (
                <Form.Text className="text-danger">
                  ❌ Le texte ne correspond pas exactement
                </Form.Text>
              )}
              {isConfirmTextValid && (
                <Form.Text className="text-success">
                  ✅ Confirmation valide
                </Form.Text>
              )}
            </Form.Group>

            <div className="text-center p-3 bg-light rounded">
              <strong className="text-danger">⚠️ DERNIÈRE CHANCE</strong>
              <br />
              <small>Une fois confirmé, il sera impossible de récupérer ces données</small>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          Annuler
        </Button>
        
        {step === 1 && (
          <Button 
            variant="warning" 
            onClick={handleNextStep}
            disabled={loading}
          >
            Continuer vers la confirmation
          </Button>
        )}
        
        {step === 2 && (
          <Button 
            variant="danger" 
            onClick={handleConfirm}
            disabled={!isConfirmTextValid || loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Suppression en cours...
              </>
            ) : (
              <>
                <Trash2 size={16} className="me-2" />
                ✅ CONFIRMER LA SUPPRESSION COMPLÈTE
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteSalesConfirmationModal;